import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, RefreshCw, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import useReducedMotion from '../../hooks/useReducedMotion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/**
 * Extracts sentence data from various backend shapes.
 * @param {Object} lesson
 * @returns {Array<{id, scrambled, correct}>|null}
 */
function getSentences(lesson) {
  const sentences =
    lesson?.content?.sentences ||
    lesson?.drag_drop_sentences ||
    null;

  if (sentences && Array.isArray(sentences) && sentences.length > 0) {
    return sentences.map((s, i) => ({
      id: s.id || i + 1,
      scrambled: Array.isArray(s.scrambled) ? s.scrambled : (s.words || []),
      correct: Array.isArray(s.correct) ? s.correct : (s.correctOrder || s.correct_order || []),
    }));
  }

  // Single sentence (legacy shape)
  const scrambled =
    lesson?.content?.scrambled ||
    lesson?.drag_drop_words ||
    null;
  const correct =
    lesson?.content?.correctOrder ||
    lesson?.content?.correct_order ||
    lesson?.correct_order ||
    null;

  if (scrambled && correct) {
    return [{ id: 1, scrambled, correct }];
  }

  return null;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * DragDropLesson — click-based word ordering (lesson_type === 'drag_drop').
 *
 * Backend content shape (Faz 4e ile gelecek):
 *   lesson.content.sentences: [{ scrambled: string[], correct: string[] }]
 *
 * Tıklamalı mod: kelimeye tıklayınca hedefe taşınır, tekrar tıklayınca geri döner.
 *
 * @param {{ lesson: Object, courseId: string, onComplete: Function, completedLessonIds: string[] }} props
 */
export default function DragDropLesson({ lesson, courseId, onComplete, completedLessonIds = [] }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const reduced = useReducedMotion();

  const lessonId = lesson?.lesson_id;
  const isCompleted = completedLessonIds.includes(lessonId);

  const rawSentences = getSentences(lesson);
  const hasContent = rawSentences !== null && rawSentences.length > 0;
  const sentences = hasContent ? rawSentences : [];

  const [sentenceIndex, setSentenceIndex] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [allFinished, setAllFinished] = useState(false);
  const [completing, setCompleting] = useState(false);

  const current = sentences[sentenceIndex];

  const buildWordState = useCallback((sentence) => ({
    pool: sentence ? shuffle(sentence.scrambled.map((w, i) => ({ word: w, key: `pool-${i}` }))) : [],
    placed: [],
    result: null, // null | 'correct' | 'incorrect'
  }), []);

  const [wordState, setWordState] = useState(() => buildWordState(current || null));

  const moveToPlaced = (item) => {
    if (wordState.result) return;
    setWordState(s => ({
      ...s,
      pool: s.pool.filter(w => w.key !== item.key),
      placed: [...s.placed, item],
    }));
  };

  const moveToPool = (item) => {
    if (wordState.result) return;
    setWordState(s => ({
      ...s,
      placed: s.placed.filter(w => w.key !== item.key),
      pool: [...s.pool, item],
    }));
  };

  const checkAnswer = () => {
    const placedWords = wordState.placed.map(w => w.word);
    const isCorrect = placedWords.length === current.correct.length &&
      placedWords.every((w, i) => w.toLowerCase() === current.correct[i].toLowerCase());
    setWordState(s => ({ ...s, result: isCorrect ? 'correct' : 'incorrect' }));
  };

  const retry = () => {
    setWordState(buildWordState(current));
  };

  const nextSentence = () => {
    if (wordState.result === 'correct') {
      setCorrectCount(c => c + 1);
    }
    if (sentenceIndex < sentences.length - 1) {
      const next = sentences[sentenceIndex + 1];
      setSentenceIndex(i => i + 1);
      setWordState(buildWordState(next));
    } else {
      const finalCorrect = correctCount + (wordState.result === 'correct' ? 1 : 0);
      setCorrectCount(finalCorrect);
      setAllFinished(true);
    }
  };

  const handleComplete = async () => {
    if (completing) return;
    setCompleting(true);
    const score = sentences.length > 0 ? Math.round((correctCount / sentences.length) * 100) : 0;
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId, quiz_score: score },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Ders tamamlandı!' : 'Lesson completed!');
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCompleting(false);
    }
  };

  const restartAll = () => {
    setSentenceIndex(0);
    setCorrectCount(0);
    setAllFinished(false);
    setWordState(buildWordState(sentences[0]));
  };

  if (isCompleted) {
    return (
      <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-6 mb-6 text-center" data-testid="dragdrop-already-completed">
        <CheckCircle size={32} className="mx-auto text-green-600 mb-2" />
        <p className="font-bold text-sm text-green-700">
          {lang === 'tr' ? 'Bu ders tamamlandı' : 'This lesson is completed'}
        </p>
      </div>
    );
  }

  if (!hasContent) {
    return (
      <div className="text-center py-12" data-testid="dragdrop-empty-state">
        <CheckCircle size={40} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500 font-semibold mb-4">
          {lang === 'tr' ? 'Bu derste henüz sürükle-bırak içeriği tanımlanmamış.' : 'No drag-drop content has been defined for this lesson yet.'}
        </p>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleComplete}
          disabled={completing}
          className="px-6 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50"
          data-testid="dragdrop-empty-complete-btn"
        >
          {completing ? '...' : t('common.completeLesson')}
        </motion.button>
      </div>
    );
  }

  return (
    <div data-testid="dragdrop-lesson">
      {/* All finished screen */}
      <AnimatePresence>
        {allFinished && (
          <motion.div
            initial={reduced ? {} : { opacity: 0, scale: 0.9 }}
            animate={reduced ? {} : { opacity: 1, scale: 1 }}
            className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-8 text-center"
            data-testid="dragdrop-completed"
          >
            <div className="w-20 h-20 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-white" />
            </div>
            <h2 className="font-display text-3xl font-bold text-amber-700 mb-1">
              {t('dragDropLesson.completed')}
            </h2>
            <p className="text-gray-600 font-semibold mb-6">
              {correctCount}/{sentences.length} — {t('dragDropLesson.score')}: %{Math.round((correctCount / sentences.length) * 100)}
            </p>
            <div className="flex justify-center gap-4">
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={handleComplete}
                disabled={completing}
                className="px-8 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid="dragdrop-complete-btn"
              >
                <CheckCircle size={18} />
                {completing ? '...' : t('common.completeLesson')}
              </motion.button>
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={restartAll}
                className="px-6 py-3 rounded-xl border-2 border-gray-300 bg-white font-bold shadow-[0_4px_0_#d1d5db] active:shadow-none active:translate-y-1 transition-all flex items-center gap-2 text-gray-700"
                data-testid="dragdrop-restart-btn"
              >
                <RefreshCw size={16} /> {t('dragDropLesson.retry')}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!allFinished && current && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-bold text-gray-500">
              {t('dragDropLesson.sentence')} {sentenceIndex + 1}/{sentences.length}
            </span>
            {wordState.result === 'correct' && (
              <span className="flex items-center gap-1 text-emerald-600 font-bold text-sm">
                <CheckCircle size={16} /> {t('dragDropLesson.correct')}
              </span>
            )}
            {wordState.result === 'incorrect' && (
              <span className="flex items-center gap-1 text-rose-500 font-bold text-sm">
                <X size={16} /> {t('dragDropLesson.incorrect')}
              </span>
            )}
          </div>

          {/* Target area */}
          <div
            className={`min-h-[64px] rounded-2xl border-2 p-3 mb-4 flex flex-wrap gap-2 transition-colors ${
              wordState.result === 'correct'
                ? 'border-emerald-400 bg-emerald-50'
                : wordState.result === 'incorrect'
                ? 'border-rose-400 bg-rose-50'
                : 'border-dashed border-cyan-300 bg-cyan-50'
            }`}
            aria-label={lang === 'tr' ? 'Cümle alanı' : 'Sentence area'}
            data-testid="dragdrop-target-area"
          >
            {wordState.placed.length === 0 && !wordState.result && (
              <span className="text-xs text-gray-400 font-semibold self-center w-full text-center">
                {t('dragDropLesson.dropHere')}
              </span>
            )}
            {wordState.placed.map((item) => (
              <motion.button
                key={item.key}
                initial={reduced ? {} : { scale: 0.8, opacity: 0 }}
                animate={reduced ? {} : { scale: 1, opacity: 1 }}
                whileTap={reduced ? {} : (wordState.result ? {} : { scale: 0.9 })}
                onClick={() => moveToPool(item)}
                disabled={!!wordState.result}
                className={`px-3 py-1.5 rounded-xl border-2 font-bold text-sm transition-all ${
                  wordState.result === 'correct'
                    ? 'border-emerald-400 bg-emerald-100 text-emerald-700 cursor-default'
                    : wordState.result === 'incorrect'
                    ? 'border-rose-400 bg-rose-100 text-rose-700 cursor-default'
                    : 'border-cyan-400 bg-white text-cyan-700 shadow-[0_2px_0_#06b6d4] hover:bg-cyan-50 cursor-pointer'
                }`}
                aria-label={`${item.word} — ${lang === 'tr' ? 'geri koy' : 'remove'}`}
                data-testid={`dragdrop-placed-${item.key}`}
              >
                {item.word}
              </motion.button>
            ))}
          </div>

          {/* Correct answer on incorrect */}
          {wordState.result === 'incorrect' && (
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 px-4 py-2 mb-4 text-sm font-semibold text-emerald-700">
              {lang === 'tr' ? 'Doğru sıra: ' : 'Correct order: '}
              <span className="font-bold">{current.correct.join(' ')}</span>
            </div>
          )}

          {/* Word pool */}
          <div
            className="flex flex-wrap gap-2 mb-6 min-h-[48px]"
            aria-label={lang === 'tr' ? 'Kelime havuzu' : 'Word pool'}
            data-testid="dragdrop-word-pool"
          >
            {wordState.pool.map((item) => (
              <motion.button
                key={item.key}
                initial={reduced ? {} : { scale: 0.8, opacity: 0 }}
                animate={reduced ? {} : { scale: 1, opacity: 1 }}
                whileHover={reduced ? {} : { scale: 1.05 }}
                whileTap={reduced ? {} : { scale: 0.95 }}
                onClick={() => moveToPlaced(item)}
                disabled={!!wordState.result}
                className="px-3 py-1.5 rounded-xl border-2 border-gray-300 bg-white text-gray-700 font-bold text-sm shadow-[0_3px_0_#d1d5db] hover:border-amber-400 hover:bg-amber-50 active:shadow-none active:translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={`${item.word} — ${lang === 'tr' ? 'yerleştir' : 'place'}`}
                data-testid={`dragdrop-pool-${item.key}`}
              >
                {item.word}
              </motion.button>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 justify-end">
            {!wordState.result && (
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={checkAnswer}
                disabled={wordState.placed.length === 0}
                className="px-8 py-3 rounded-xl border-2 border-cyan-500 bg-cyan-500 text-white font-bold shadow-[0_4px_0_#0891b2] active:shadow-none active:translate-y-1 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="dragdrop-check-btn"
              >
                {t('dragDropLesson.check')}
              </motion.button>
            )}

            {wordState.result === 'incorrect' && (
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={retry}
                className="px-6 py-3 rounded-xl border-2 border-rose-400 bg-white text-rose-500 font-bold shadow-[0_4px_0_#fb7185] active:shadow-none active:translate-y-1 transition-all flex items-center gap-2"
                data-testid="dragdrop-retry-btn"
              >
                <RefreshCw size={16} /> {t('dragDropLesson.retry')}
              </motion.button>
            )}

            {wordState.result && (
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={nextSentence}
                className="px-8 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all"
                data-testid="dragdrop-next-btn"
              >
                {sentenceIndex < sentences.length - 1
                  ? t('dragDropLesson.nextSentence')
                  : t('dragDropLesson.submitAll')}
              </motion.button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
